import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { Reveal } from "../Reveal";
import { scrollToId } from "../../lib/scroll";

const CLIENTS = [
    { id: 0, src: "/showcase/client-1.jpg", alt: "Client billboard on a highway digital billboard" },
    { id: 1, src: "/showcase/client-2.jpg", alt: "Client billboard displayed above a roadside" },
    { id: 2, src: "/showcase/client-3.jpg", alt: "Client billboard illuminated at night" },
];

export const FunnelClients = () => {
    const trackRef = useRef(null);
    const [active, setActive] = useState(0);
    const [lightbox, setLightbox] = useState(null);
    const pausedRef = useRef(false);
    const resumeTimer = useRef(null);

    const pauseTemporarily = useCallback(() => {
        pausedRef.current = true;
        clearTimeout(resumeTimer.current);
        resumeTimer.current = setTimeout(() => {
            pausedRef.current = false;
        }, 8000);
    }, []);

    useEffect(() => () => clearTimeout(resumeTimer.current), []);

    useEffect(() => {
        const t = setInterval(() => {
            if (pausedRef.current || lightbox) return;
            const el = trackRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            if (rect.bottom < 0 || rect.top > window.innerHeight) return;
            goTo((active + 1) % CLIENTS.length);
        }, 4500);
        return () => clearInterval(t);
    }, [active, lightbox]);

    const onScroll = useCallback(() => {
        const el = trackRef.current;
        if (!el) return;
        const slide = el.querySelector("[data-slide]");
        if (!slide) return;
        const w = slide.getBoundingClientRect().width + 16;
        setActive(Math.min(CLIENTS.length - 1, Math.max(0, Math.round(el.scrollLeft / w))));
    }, []);

    const goTo = (i) => {
        const el = trackRef.current;
        const slide = el?.querySelectorAll("[data-slide]")[i];
        if (slide) el.scrollTo({ left: slide.offsetLeft - el.offsetLeft, behavior: "smooth" });
    };

    return (
        <section id="client-showcase" className="relative bg-noir py-24 sm:py-32 overflow-hidden" data-testid="client-showcase">
            <div className="absolute inset-0 led-dots opacity-25" aria-hidden="true" />
            <div className="absolute top-0 left-1/3 h-[360px] w-[360px] rounded-full bg-hotpink/10 blur-[120px]" aria-hidden="true" />

            <div className="relative mx-auto max-w-[1200px] px-4 sm:px-6">
                <Reveal>
                    <p className="text-center font-alt text-[10px] font-bold tracking-[0.4em] text-hotpink uppercase" data-testid="client-showcase-eyebrow">
                        REAL CLIENTS. REAL PLACEMENTS.
                    </p>
                </Reveal>
                <Reveal delay={0.08}>
                    <h2 className="mt-4 text-center font-display text-5xl sm:text-6xl font-extrabold tracking-wide leading-[0.98] text-white" data-testid="client-showcase-header">
                        OUR CLIENTS ARE <span className="text-hotpink led-text-glow">GETTING SEEN.</span>
                    </h2>
                </Reveal>
                <Reveal delay={0.14}>
                    <p className="mt-4 text-center font-alt text-xs sm:text-sm font-semibold tracking-[0.18em] text-zinc-400 uppercase">
                        Real Businesses. Real Billboards. Real-World Visibility.
                    </p>
                </Reveal>

                <Reveal delay={0.2}>
                    <div
                        ref={trackRef}
                        onScroll={onScroll}
                        onTouchStart={pauseTemporarily}
                        onPointerDown={pauseTemporarily}
                        onWheel={pauseTemporarily}
                        className="no-scrollbar mt-12 flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-4 px-4 sm:mx-0 sm:px-0"
                        data-testid="client-carousel"
                    >
                        {CLIENTS.map((c, i) => (
                            <figure key={c.id} data-slide className="w-[92%] sm:w-[68%] lg:w-[46%] shrink-0 snap-center" data-testid={`client-slide-${i}`}>
                                <button
                                    type="button"
                                    onClick={() => setLightbox(c)}
                                    className="block w-full cursor-zoom-in rounded-2xl overflow-hidden shadow-[0_24px_70px_rgba(0,0,0,0.6)] border border-white/10 transition-shadow duration-300 hover:shadow-[0_24px_70px_rgba(255,255,255,0.15)]"
                                    data-testid={`client-image-${i}`}
                                    aria-label={`View larger: ${c.alt}`}
                                >
                                    <img
                                        src={c.src}
                                        alt={c.alt}
                                        loading={i === 0 ? "eager" : "lazy"}
                                        decoding="async"
                                        className="h-auto w-full block"
                                    />
                                </button>
                                <figcaption className="mt-4 text-center">
                                    <p className="font-alt text-[10px] font-bold tracking-[0.32em] text-hotpink">CLIENT BILLBOARD</p>
                                    <p className="mt-1 text-sm font-semibold text-white">Digital Billboard Campaign</p>
                                    <p className="mt-0.5 font-alt text-[10px] tracking-[0.24em] text-zinc-500 uppercase">WealthHaus Media</p>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </Reveal>

                <div className="mt-7 flex justify-center gap-2.5" data-testid="client-dots">
                    {CLIENTS.map((c, i) => (
                        <button
                            key={c.id}
                            onClick={() => { pauseTemporarily(); goTo(i); }}
                            aria-label={`Go to client billboard ${i + 1}`}
                            className={`h-2 rounded-full transition-all duration-300 ${
                                active === i ? "w-8 bg-hotpink led-glow" : "w-2 bg-white/25 hover:bg-white/45"
                            }`}
                            data-testid={`client-dot-${i}`}
                        />
                    ))}
                </div>

                <Reveal delay={0.15}>
                    <div className="mt-14 text-center">
                        <p className="font-alt text-xs sm:text-sm font-bold tracking-[0.22em] text-zinc-300 uppercase" data-testid="client-cta-lead">
                            WANT YOUR BUSINESS ON A BILLBOARD?
                        </p>
                        <button
                            onClick={() => scrollToId("#quote")}
                            className="btn-glossy-pink mt-5 !px-10 !py-4 !text-sm"
                            data-testid="client-cta-button"
                        >
                            GET MY BUSINESS SEEN <ArrowRight size={16} />
                        </button>
                    </div>
                </Reveal>
            </div>

            {lightbox && (
                <div
                    className="fixed inset-0 z-[70] flex items-center justify-center bg-noir/95 backdrop-blur-md p-4"
                    onClick={() => setLightbox(null)}
                    data-testid="client-lightbox"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Enlarged client billboard view"
                >
                    <button
                        className="absolute top-4 right-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white hover:bg-hotpink hover:text-noir transition-colors"
                        onClick={() => setLightbox(null)}
                        aria-label="Close"
                        data-testid="client-lightbox-close"
                    >
                        <X size={20} />
                    </button>
                    <img
                        src={lightbox.src}
                        alt={lightbox.alt}
                        className="max-h-[86vh] max-w-full rounded-xl shadow-2xl object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </section>
    );
};
